import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { MapPinIcon, HomeIcon, StarIcon, Heart, HandHeart, Users, Activity, AlertTriangle, HeartHandshake, HeartPulseIcon, ShoppingBasket, Stethoscope, Building2, BatteryLow, Radio, Pill, HandCoins, Share2, HeartCrack, Globe2 } from 'lucide-react';
const LOGO = 'https://cdn.magicpatterns.com/uploads/owciZka94T3FY4LQCQ9qcS/pancita_feliz.png';
const HERO_BG = 'https://cdn.magicpatterns.com/uploads/tJ9Qyo76BSSU8qBwrWJ4Ft/ChatGPT_Image_Jun_29,_2026,_01_10_38_PM.png';
const stats = [{
  number: '+12,000',
  label: 'Desplazados',
  color: '#7DC920',
  border: true,
  icon: Users,
  iconColor: '#7DC920'
}, {
  number: '+1,500',
  label: 'Fallecidos',
  color: '#ff6b6b',
  border: true,
  icon: HeartCrack,
  iconColor: '#ff6b6b'
}, {
  number: '24+',
  label: 'Países',
  color: '#a8e063',
  border: true,
  icon: Globe2,
  iconColor: '#a8e063'
}, {
  number: '7.5',
  label: 'Magnitud',
  color: '#7DC920',
  border: false,
  icon: Activity,
  iconColor: '#7DC920'
}];
const timeline = [{
  dot: '#2d5a1b',
  time: '24 Jun · Hora 0',
  title: 'Primer Terremoto 7.2',
  text: 'Epicentro al oeste de Caracas, entre Carabobo y Yaracuy'
}, {
  dot: '#2d5a1b',
  time: '24 Jun · +39 segundos',
  title: 'Segundo Terremoto 7.5',
  text: 'El más fuerte, causó el colapso masivo de edificios residenciales'
}, {
  dot: '#a8e063',
  time: '24 Jun · Horas después',
  title: 'Alerta de Tsunami',
  text: 'Emitida para el Caribe, cancelada horas después sin daños por tsunami'
}, {
  dot: '#7DC920',
  time: '29 Jun',
  title: 'Réplica 4.6',
  text: 'Al norte de Caracas, sin daños adicionales importantes'
}, {
  dot: '#7DC920',
  time: '29 Jun · 106 horas después',
  title: 'Rescate Milagroso',
  text: 'Joven de 21 años encontrado con vida tras 106 horas bajo los escombros'
}];
const zones = [{
  title: 'Caracas',
  text: 'Capital. Colapso de edificios y daños en infraestructura',
  badge: 'CRÍTICO',
  badgeBg: '#e8f5e0',
  badgeColor: '#2d5a1b'
}, {
  title: 'La Guaira',
  text: 'Zona costera con daños severos en puerto e infraestructura',
  badge: 'CRÍTICO',
  badgeBg: '#e8f5e0',
  badgeColor: '#2d5a1b'
}, {
  title: 'Carabobo',
  text: 'Daños en hospitales y carreteras principales',
  badge: 'GRAVE',
  badgeBg: '#f0f9e0',
  badgeColor: '#5a8a1b'
}, {
  title: 'Yaracuy',
  text: 'Derrumbes y cortes de electricidad y comunicaciones',
  badge: 'GRAVE',
  badgeBg: '#f0f9e0',
  badgeColor: '#5a8a1b'
}];
const needs = [{
  icon: ShoppingBasket,
  title: 'Alimentación',
  text: 'Familias sin acceso a comida ni agua potable en zonas devastadas'
}, {
  icon: Stethoscope,
  title: 'Atención Médica',
  text: 'Hospitales dañados y más de 3,000 heridos necesitan atención urgente'
}, {
  icon: Building2,
  title: 'Refugio',
  text: 'Más de 12,000 desplazados sin hogar necesitan albergue temporal seguro'
}, {
  icon: BatteryLow,
  title: 'Electricidad',
  text: 'Cortes masivos de luz afectan hospitales y comunicaciones de emergencia'
}, {
  icon: Radio,
  title: 'Comunicaciones',
  text: 'Zonas incomunicadas dificultan las labores de rescate y localización de desaparecidos'
}, {
  icon: Pill,
  title: 'Medicamentos',
  text: 'Escasez crítica de medicinas e insumos médicos en toda la zona afectada'
}];
const sectionLabel: React.CSSProperties = {
  fontSize: '12px',
  color: '#7DC920',
  letterSpacing: '3px',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '12px',
  textTransform: 'uppercase'
};
const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px 16px',
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  fontSize: '15px',
  marginBottom: '16px',
  outline: 'none'
};
export function VenezuelaEmergencia() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  useDocumentMeta({
    title: 'Emergencia Venezuela | Pancita Feliz Foundation',
    description: 'Venezuela necesita tu ayuda. Dos terremotos devastaron el país dejando más de 12,000 familias sin hogar. Dona ahora.',
    path: '/venezuela-emergencia'
  });
  const handleVolunteerSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    try {
      await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: {
          Accept: 'application/json'
        }
      });
      setFormSubmitted(true);
      form.reset();
    } catch {
      setFormSubmitted(true);
    }
  };
  return <main className="w-full" data-id="element-615" data-mp="21eczw47ltxq2">
      {/* SECTION 1: HERO */}
      <section className="mp-emg-hero-section relative overflow-hidden flex items-center justify-center" style={{
      minHeight: '100vh',
      backgroundColor: '#111111'
    }} data-id="element-616" data-mp="26d6bts78tgo4">
        {/* Background image layer */}
        <div className="absolute inset-0" style={{
        backgroundImage: `url('${HERO_BG}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: 0
      }} data-id="element-617" data-mp="2bkw8hpdmfm2a" />
        {/* Neutral cinematic overlay with vignette */}
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(circle at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 100%)',
        zIndex: 1
      }} data-id="element-618" data-mp="1ss24trlg7gdd" />

        {/* Content */}
        <div className="mp-emg-hero-content relative flex flex-col items-center justify-center text-center w-full px-4 animate-fade-in-down" style={{
        zIndex: 2,
        maxWidth: '760px',
        paddingTop: '100px',
        paddingBottom: '60px'
      }} data-id="element-619" data-mp="1jtzz4yqrrzjr">
          <img src={LOGO} alt="Pancita Feliz Foundation" className="mp-emg-hero-logo" style={{
          width: '200px',
          maxWidth: '200px',
          height: 'auto',
          filter: 'brightness(0) invert(1)',
          display: 'block',
          margin: '0 auto 28px auto'
        }} data-id="element-620" data-mp="1g9fzmo43f09l" />

          <span className="mp-emg-pulse flex items-center gap-2" style={{
          background: '#C94A44',
          color: 'white',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '1px',
          padding: '8px 20px',
          borderRadius: '9999px',
          display: 'inline-flex',
          marginBottom: '24px'
        }} data-id="element-621" data-mp="1z8lj1kt69hs2">
            <span data-id="element-622" data-mp="etrxxo89qrfm">⚠️</span> EMERGENCIA ACTIVA
          </span>

          <div className="relative mb-6" data-id="element-623" data-mp="6bpxoahqaj8j">
            <h1 className="mp-emg-hero-title" style={{
            fontSize: 'clamp(40px, 8vw, 72px)',
            fontWeight: 800,
            color: '#FFFFFF',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: 0
          }} data-id="element-624" data-mp="6aok9zd8pknn">
              Venezuela Nos Necesita
            </h1>
            <div className="mp-emg-hero-tagline" style={{
            fontFamily: "'Caveat', cursive",
            color: '#7AC943',
            fontSize: 'clamp(24px, 4vw, 36px)',
            position: 'absolute',
            right: '-20px',
            bottom: '-20px',
            transform: 'rotate(-5deg)',
            whiteSpace: 'nowrap'
          }} data-id="element-625" data-mp="3jpumuwqmbz68">
              Hoy más que nunca{' '}
              <HandHeart size={28} color="#7DC920" style={{
              display: 'inline-block',
              verticalAlign: 'middle'
            }} data-id="element-626" data-mp="2w1bg4ln7vug9" />
            </div>
          </div>

          <p className="mp-emg-hero-subtitle" style={{
          fontSize: '18px',
          color: 'rgba(255,255,255,0.9)',
          maxWidth: '620px',
          margin: '0 auto 40px',
          lineHeight: 1.8,
          fontWeight: 400
        }} data-id="element-627" data-mp="2fh20v35lfrg1">
            Dos terremotos de magnitud 7.2 y 7.5 han dejado miles de familias
            sin hogar y con necesidades urgentes.
            <br data-id="element-628" data-mp="335upsfnssz15" />
            <br data-id="element-629" data-mp="337fis0ugs9ww" />
            Tu ayuda puede llevar esperanza, alimento, refugio y apoyo a cada
            familia afectada.
          </p>

          {/* Impact Icons */}
          <div className="mp-emg-hero-pills flex flex-wrap justify-center gap-6 md:gap-8 mb-10" data-id="element-630" data-mp="3d4u1k73v1asd">
            {[{
            icon: Users,
            text: 'Para todas las familias'
          }, {
            icon: HeartPulseIcon,
            text: 'Apoyo integral'
          }, {
            icon: HeartHandshake,
            text: 'Esperanza que une'
          }, {
            icon: MapPinIcon,
            text: 'Donde más se necesita'
          }].map((item, i) => <div key={i} className="flex items-center gap-2" data-id="element-631" data-mp="1p6jid1r8b5zx" data-mpi={i}>
                <item.icon size={20} color="#7AC943" strokeWidth={1.5} data-id="element-632" data-mp="1p601lodbennh" />
                <span style={{
              color: 'white',
              fontSize: '14px',
              fontWeight: 500
            }} data-id="element-633" data-mp="1k915x1e068n6">
                  {item.text}
                </span>
              </div>)}
          </div>

          {/* CTAs */}
          <div className="mp-emg-hero-btns flex flex-col sm:flex-row justify-center gap-4 w-full mb-16" data-id="element-634" data-mp="27ylsrvda1q1q">
            <Link to="/donar-venezuela" className="mp-emg-hero-btn group relative overflow-hidden flex flex-col items-center justify-center transition-transform hover:-translate-y-1" style={{
            background: '#7AC943',
            color: 'white',
            borderRadius: '16px',
            height: '72px',
            padding: '0 40px',
            textDecoration: 'none',
            boxShadow: '0 10px 30px rgba(122, 201, 67, 0.3)',
            minWidth: '280px'
          }} data-id="element-635" data-mp="234hjh5gjh5xh">
              <div className="flex items-center gap-2 font-bold text-lg" data-id="element-636" data-mp="34pd16evrcrj9">
                <Heart size={20} fill="currentColor" data-id="element-637" data-mp="3jdr56t4wum1e" /> Donar Ahora
              </div>
              <div style={{
              fontSize: '12px',
              opacity: 0.9,
              fontWeight: 500,
              marginTop: '2px'
            }} data-id="element-638" data-mp="3efftecv221gj">
                Cada aporte hace la diferencia
              </div>
            </Link>

            <a href="#voluntario" className="mp-emg-hero-btn group relative overflow-hidden flex flex-col items-center justify-center transition-transform hover:-translate-y-1 hover:bg-white/10" style={{
            background: 'transparent',
            color: 'white',
            borderRadius: '16px',
            height: '72px',
            padding: '0 40px',
            border: '1px solid rgba(255,255,255,0.4)',
            textDecoration: 'none',
            minWidth: '280px'
          }} data-id="element-639" data-mp="awjqdu0xzid0">
              <div className="flex items-center gap-2 font-bold text-lg" data-id="element-640" data-mp="3fbsy281c81s3">
                Quiero Ser Voluntario
              </div>
              <div style={{
              fontSize: '12px',
              opacity: 0.7,
              fontWeight: 500,
              marginTop: '2px'
            }} data-id="element-641" data-mp="3jd4q0k7ooz3g">
                Únete a nuestra red de ayuda
              </div>
            </a>
          </div>

          {/* Stats Card */}
          <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-6 p-6 md:p-8" style={{
          background: 'rgba(20, 20, 20, 0.6)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '24px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          marginBottom: '24px'
        }} data-id="element-642" data-mp="1b4zyam8wk6nb">
            {[{
            num: '+1,500',
            label: 'Fallecidos',
            icon: AlertTriangle
          }, {
            num: '+3,000',
            label: 'Heridos',
            icon: Activity
          }, {
            num: '+12,000',
            label: 'Desplazados',
            icon: HomeIcon
          }, {
            num: '7.5',
            label: 'Magnitud',
            icon: Activity
          }].map((stat, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-643" data-mp="4bagaf6oawgc" data-mpi={i}>
                <div className="flex items-center justify-center mb-3" style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(122, 201, 67, 0.15)',
              color: '#7AC943'
            }} data-id="element-644" data-mp="98u5ga4wstn2">
                  <stat.icon size={20} strokeWidth={2} data-id="element-645" data-mp="s0mnj9ifwug0" />
                </div>
                <div style={{
              color: 'white',
              fontSize: '24px',
              fontWeight: 700,
              marginBottom: '4px'
            }} data-id="element-646" data-mp="ryimczbxe9jf">
                  {stat.num}
                </div>
                <div style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }} data-id="element-647" data-mp="2ysx7i2a4fg9c">
                  {stat.label}
                </div>
              </div>)}
          </div>

          {/* Bottom Statement */}
          <div className="flex items-center justify-center gap-2" style={{
          color: 'white',
          fontSize: '14px',
          opacity: 0.8
        }} data-id="element-648" data-mp="9e1keise6foo">
            <Heart size={14} color="#7AC943" fill="#7AC943" data-id="element-649" data-mp="j5edfdpxfbwa" />
            <span data-id="element-650" data-mp="edmmh22uz45m">
              Tu solidaridad se transforma en vida, esperanza y reconstrucción.
            </span>
          </div>
        </div>
      </section>

      {/* SECTION 2: STATS BAR */}
      <section style={{
      background: '#1a1a1a',
      borderBottom: '1px solid #333'
    }} data-id="element-651" data-mp="3hucvzqxnfzc2">
        <div className="mp-emg-stats" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        maxWidth: '900px',
        margin: '0 auto',
        padding: '32px 20px'
      }} data-id="element-652" data-mp="1xc2ctyc5m9ad">
          {stats.map(s => <div key={s.label} className="mp-emg-stat-item" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 16px',
          borderRight: s.border ? '1px solid #333' : 'none'
        }} data-id="element-653" data-mp="1ta79koanxlty">
              <s.icon size={24} color={s.iconColor} style={{
            marginBottom: '8px'
          }} data-id="element-654" data-mp="274husvngaxv9" />
              <div className="mp-emg-stat-num" style={{
            fontSize: '36px',
            fontWeight: 800,
            color: s.color
          }} data-id="element-655" data-mp="vlnj4q5hcyz9">
                {s.number}
              </div>
              <div className="mp-emg-stat-label" style={{
            fontSize: '13px',
            color: '#999'
          }} data-id="element-656" data-mp="cjn02iakvbi7">
                {s.label}
              </div>
            </div>)}
        </div>
      </section>

      {/* SECTION 3: WHAT HAPPENED */}
      <section className="mp-emg-what-section" style={{
      background: '#f9f9f9',
      padding: '80px 20px'
    }} data-id="element-657" data-mp="3twqvcsgxqu8x">
        <p style={sectionLabel} data-id="element-658" data-mp="3kz6on40b05qx">Lo que ocurrió</p>
        <h2 className="mp-emg-section-title" style={{
        fontSize: '36px',
        fontWeight: 700,
        color: '#1a1a1a',
        textAlign: 'center',
        marginBottom: '48px',
        maxWidth: '600px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }} data-id="element-659" data-mp="3kzzy4cqnd9cr">
          El Desastre Más Devastador de Venezuela en Décadas
        </h2>
        <div className="mp-emg-2col" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '40px',
        maxWidth: '900px',
        margin: '0 auto'
      }} data-id="element-660" data-mp="1qprs2g4s72wi">
          {/* Left: timeline */}
          <div data-id="element-661" data-mp="12scd37q2firw">
            <h3 style={{
            fontSize: '18px',
            fontWeight: 700,
            marginBottom: '24px',
            color: '#1a1a1a'
          }} data-id="element-662" data-mp="12svtul3zc14c">
              Cronología
            </h3>
            <div style={{
            borderLeft: '2px dashed #ddd',
            marginLeft: '5px'
          }} data-id="element-663" data-mp="1cp8obl7cmj05">
              {timeline.map((item, i) => <div key={i} className="flex gap-4" style={{
              marginBottom: '24px',
              marginLeft: '-7px'
            }} data-id="element-664" data-mp="2o5fgvzt5n8lj" data-mpi={i}>
                  <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                marginTop: '4px',
                flexShrink: 0,
                background: item.dot
              }} data-id="element-665" data-mp="399zortlwjqbv" />
                  <div data-id="element-666" data-mp="swl3km9ar2y">
                    <div style={{
                  fontSize: '11px',
                  color: '#2d5a1b',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  marginBottom: '2px'
                }} data-id="element-667" data-mp="s39o6ccrdeh">
                      {item.time}
                    </div>
                    <div style={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  marginBottom: '4px'
                }} data-id="element-668" data-mp="2cka0r78126rl">
                      {item.title}
                    </div>
                    <div style={{
                  fontSize: '14px',
                  color: '#666',
                  lineHeight: 1.5
                }} data-id="element-669" data-mp="s392t38aduf4">
                      {item.text}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Right: zones */}
          <div data-id="element-670" data-mp="3qj1zlv0u6nrg">
            <h3 style={{
            fontSize: '18px',
            fontWeight: 700,
            marginBottom: '24px',
            color: '#1a1a1a'
          }} data-id="element-671" data-mp="3qjlgd8er363w">
              Zonas Afectadas
            </h3>
            {zones.map(z => <div key={z.title} className="flex items-center gap-3" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '12px',
            borderLeft: '4px solid #2d5a1b',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }} data-id="element-672" data-mp="1tqjot42adqut">
                <MapPinIcon size={20} color="#2d5a1b" className="shrink-0" data-id="element-673" data-mp="2scwpipz242vs" />
                <div data-id="element-674" data-mp="3g0xvps8rj781">
                  <div style={{
                fontSize: '15px',
                fontWeight: 700,
                color: '#1a1a1a'
              }} data-id="element-675" data-mp="3g2iqniyzos6f">
                    {z.title}
                  </div>
                  <div style={{
                fontSize: '13px',
                color: '#666'
              }} data-id="element-676" data-mp="3bzm27bmrq1uk">
                    {z.text}
                  </div>
                </div>
                <span style={{
              background: z.badgeBg,
              color: z.badgeColor,
              fontSize: '10px',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '10px',
              marginLeft: 'auto',
              flexShrink: 0
            }} data-id="element-677" data-mp="20ma65mx92niy">
                  {z.badge}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4: NEEDS */}
      <section className="mp-emg-needs-section" style={{
      background: 'white',
      padding: '80px 20px'
    }} data-id="element-678" data-mp="14em6plqc803v">
        <p style={sectionLabel} data-id="element-679" data-mp="1j53ahbqg323x">Ayuda urgente necesaria</p>
        <h2 className="mp-emg-section-title" style={{
        fontSize: '36px',
        fontWeight: 700,
        textAlign: 'center',
        marginBottom: '48px',
        color: '#1a1a1a'
      }} data-id="element-680" data-mp="1eb8qs8auan4u">
          ¿Qué Necesitan las Familias?
        </h2>
        <div className="mp-emg-needs" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '24px',
        maxWidth: '900px',
        margin: '0 auto'
      }} data-id="element-681" data-mp="2lp2q1lloeti9">
          {needs.map(({
          icon: Icon,
          title,
          text
        }) => <div key={title} className="mp-emg-need-card" style={{
          background: '#f9f9f9',
          borderRadius: '16px',
          padding: '32px 24px',
          textAlign: 'center',
          borderTop: '4px solid #7DC920'
        }} data-id="element-682" data-mp="2zgr3bs7nvo0t">
              <div className="mp-emg-need-icon" style={{
            background: '#e8f5e0',
            borderRadius: '16px',
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }} data-id="element-683" data-mp="2gqrd320342vt">
                <Icon size={28} color="#2d5a1b" data-id="element-684" data-mp="16f9u04pfw00" />
              </div>
              <h3 className="mp-emg-need-title" style={{
            fontSize: '18px',
            fontWeight: 700,
            color: '#1a1a1a',
            marginBottom: '8px'
          }} data-id="element-685" data-mp="3sjtgrqhg8e3s">
                {title}
              </h3>
              <p className="mp-emg-need-text" style={{
            fontSize: '14px',
            color: '#666',
            lineHeight: 1.5
          }} data-id="element-686" data-mp="3ejr2ee8co2ta">
                {text}
              </p>
            </div>)}
        </div>
      </section>

      {/* SECTION 4b: RESCUE STORY */}
      <section style={{
      background: '#1a1a1a',
      padding: '60px 20px',
      textAlign: 'center'
    }} data-id="element-687" data-mp="17rhxhmp5isx3">
        <div className="mp-emg-rescue-box" style={{
        background: 'rgba(125,201,32,0.1)',
        border: '1px solid rgba(125,201,32,0.3)',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '600px',
        margin: '0 auto',
        textAlign: 'center'
      }} data-id="element-688" data-mp="1awgyrfhxq5tq">
          <StarIcon size={32} color="#7DC920" className="mx-auto" style={{
          marginBottom: '16px'
        }} data-id="element-689" data-mp="15y73l4w7n584" />
          <h3 style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'white',
          marginBottom: '12px'
        }} data-id="element-690" data-mp="wxr0hwjdq03s">
            Historias de Esperanza
          </h3>
          <p className="mp-emg-rescue-text" style={{
          fontSize: '15px',
          color: 'rgba(255,255,255,0.8)',
          lineHeight: 1.7
        }} data-id="element-691" data-mp="95k08maj98z3">
            En medio de la tragedia, rescatistas venezolanos e internacionales
            trabajan sin descanso. Cada hora cuenta. Cada vida importa. Tu apoyo
            hace posible que estas historias continúen escribiéndose.
          </p>
        </div>
      </section>

      {/* SECTION 5: FAMILIES THAT NEED HELP */}
      <section className="mp-emg-family-section" style={{
      background: '#1a1a1a',
      padding: '60px 20px'
    }} data-id="element-692" data-mp="3vmakg6fxh9w3">
        <div className="mp-emg-family" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0,
        maxWidth: '1000px',
        margin: '0 auto',
        alignItems: 'center'
      }} data-id="element-693" data-mp="8jnyde1vq701">
          {/* Left: text content */}
          <div className="mp-emg-family-left" style={{
          padding: '40px'
        }} data-id="element-694" data-mp="w9s17j78u2pz">
            <div style={{
            fontSize: '12px',
            color: '#7DC920',
            letterSpacing: '3px',
            fontWeight: 700,
            marginBottom: '16px'
          }} data-id="element-695" data-mp="rbgnuyhqpa30">
              FAMILIAS QUE NECESITAN AYUDA
            </div>
            <h2 className="mp-emg-family-title" style={{
            fontSize: '32px',
            fontWeight: 800,
            color: 'white',
            lineHeight: 1.2,
            marginBottom: '20px'
          }} data-id="element-696" data-mp="3u07bfyprtfht">
              Detrás de Cada Número, Hay una Familia
            </h2>
            <p className="mp-emg-family-text" style={{
            fontSize: '16px',
            color: 'rgba(255,255,255,0.75)',
            lineHeight: 1.7,
            marginBottom: '32px'
          }} data-id="element-697" data-mp="hly9hkf0b3pg">
              Más de 12,000 personas perdieron su hogar en segundos. Familias
              con niños, adultos mayores y personas vulnerables que hoy
              necesitan alimento, refugio y esperanza.
            </p>
            <div className="mp-emg-family-stats" style={{
            display: 'flex',
            gap: '32px'
          }} data-id="element-698" data-mp="2b3dmi614bywz">
              {[{
              number: '+12,000',
              label: 'Desplazados',
              color: '#7DC920'
            }, {
              number: '+1,500',
              label: 'Fallecidos',
              color: 'white'
            }, {
              number: '24+',
              label: 'Países ayudando',
              color: '#a8e063'
            }].map(s => <div key={s.label} className="mp-emg-family-stat" data-id="element-699" data-mp="1ed3cewqnfzgb">
                  <div className="mp-emg-family-stat-num" style={{
                fontSize: '28px',
                fontWeight: 800,
                color: s.color
              }} data-id="element-700" data-mp="1eba3faskd355">
                    {s.number}
                  </div>
                  <div style={{
                fontSize: '12px',
                color: '#999'
              }} data-id="element-701" data-mp="2zibuafrhe509">
                    {s.label}
                  </div>
                </div>)}
            </div>
          </div>

          {/* Right: image */}
          <div className="mp-emg-family-img" style={{
          position: 'relative',
          height: '400px',
          overflow: 'hidden'
        }} data-id="element-702" data-mp="10zis1sxb8o9">
            <img src="https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&q=80" alt="Familia afectada por la emergencia" style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }} data-id="element-703" data-mp="3nfizw69a91yf" />
            <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, rgba(26,26,26,0.4) 0%, transparent 100%)'
          }} data-id="element-704" data-mp="24gbi8bdfal97" />
          </div>
        </div>
      </section>

      {/* SECTION 6: DONATION CTA */}
      <section className="mp-emg-cta-section" style={{
      background: 'linear-gradient(135deg, #1a3a0a 0%, #2d5a1b 100%)',
      padding: '80px 20px',
      textAlign: 'center'
    }} data-id="element-705" data-mp="29ap6zuxkxu4w">
        <h2 className="mp-emg-cta-title" style={{
        fontSize: '42px',
        fontWeight: 800,
        color: 'white',
        marginBottom: '16px'
      }} data-id="element-706" data-mp="1ujyf6mvzmv1s">
          Tu Donación Salva Vidas
        </h2>
        <p className="mp-emg-cta-subtitle" style={{
        fontSize: '18px',
        color: 'rgba(255,255,255,0.85)',
        marginBottom: '40px'
      }} data-id="element-707" data-mp="360ormnr08j3u">
          Cada aporte llega directamente a las familias venezolanas más
          afectadas por esta tragedia.
        </p>
        <div className="mp-emg-btns flex flex-wrap justify-center gap-4" data-id="element-708" data-mp="36l5i9v5mhugl">
          <Link to="/donar-venezuela" className="mp-emg-btn" style={{
          background: 'white',
          color: '#2d5a1b',
          fontWeight: 700,
          padding: '18px 48px',
          borderRadius: '12px',
          fontSize: '18px',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }} data-id="element-709" data-mp="36nhz24zn9ys2">
            <HandCoins size={20} color="#2d5a1b" data-id="element-710" data-mp="3kkdlcz4wpmq2" /> Donar Ahora
          </Link>
          <button type="button" className="mp-emg-btn" onClick={() => {
          if (navigator.share) {
            navigator.share({
              title: 'Venezuela Emergencia',
              text: 'Venezuela necesita tu ayuda.',
              url: window.location.href
            });
          } else {
            navigator.clipboard.writeText(window.location.href).then(() => {
              alert('¡Enlace copiado!');
            });
          }
        }} style={{
          background: 'transparent',
          color: 'white',
          border: '2px solid white',
          fontWeight: 700,
          padding: '18px 48px',
          borderRadius: '12px',
          fontSize: '18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }} data-id="element-711" data-mp="lvg31h10c6nd">
            <Share2 size={20} data-id="element-712" data-mp="1r2g0ejmrsous" /> Compartir esta página
          </button>
        </div>
      </section>

      {/* SECTION 7: VOLUNTEER FORM */}
      <section id="voluntario" className="mp-emg-vol-section" style={{
      background: '#f9f9f9',
      padding: '80px 20px'
    }} data-id="element-713" data-mp="1v52ukr6wylv5">
        <p style={sectionLabel} data-id="element-714" data-mp="3n646960bnbe3">Únete al esfuerzo</p>
        <h2 className="mp-emg-form-title" style={{
        fontSize: '36px',
        fontWeight: 700,
        textAlign: 'center',
        marginBottom: '8px',
        color: '#1a1a1a'
      }} data-id="element-715" data-mp="3n3hpcfrjjjj7">
          ¿Quieres Ser Voluntario?
        </h2>
        <p style={{
        fontSize: '16px',
        color: '#666',
        textAlign: 'center',
        marginBottom: '48px'
      }} data-id="element-716" data-mp="eny340paf1dn">
          Regístrate y te contactaremos con formas concretas de ayudar
        </p>
        <form action="https://formspree.io/f/mojolpgn" method="POST" onSubmit={handleVolunteerSubmit} className="mp-emg-form" style={{
        maxWidth: '560px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
      }} data-id="element-717" data-mp="jpx4j51gvo7y">
          <input type="hidden" name="_subject" value="Nuevo Voluntario - Emergencia Venezuela" data-id="element-718" data-mp="2fddp5zuvrak8" />
          <input type="text" name="nombre" required placeholder="Tu nombre completo" aria-label="Nombre completo" className="mp-emg-field" style={fieldStyle} data-id="element-719" data-mp="26llhfwx8qfjc" />
          <input type="email" name="email" required placeholder="tu@email.com" aria-label="Email" className="mp-emg-field" style={fieldStyle} data-id="element-720" data-mp="1woyzph07o4lj" />
          <input type="text" name="pais" placeholder="¿Desde dónde nos escribes?" aria-label="País de residencia" className="mp-emg-field" style={fieldStyle} data-id="element-721" data-mp="1nok9q4cay1bp" />
          <select name="como_ayudar" aria-label="¿Cómo quieres ayudar?" defaultValue="" className="mp-emg-field" style={fieldStyle} data-id="element-722" data-mp="1hxg09c55pv3v">
            <option value="" data-id="element-723" data-mp="17z9x6r7m6ixf">Selecciona una opción</option>
            <option value="donacion" data-id="element-724" data-mp="13yq0tpuv7jcc">Donación económica</option>
            <option value="difusion" data-id="element-725" data-mp="13ygcs7tdrm9a">Difusión en redes sociales</option>
            <option value="organizaciones" data-id="element-726" data-mp="z0ojb9d37s2z">Contacto con organizaciones</option>
            <option value="otro" data-id="element-727" data-mp="u8emrahgvtrq">Otros</option>
          </select>
          <textarea name="comentario" rows={4} placeholder="¿Algo más que quieras contarnos? (opcional)" aria-label="Comentario" className="mp-emg-field mp-emg-textarea" style={{
          ...fieldStyle,
          fontFamily: 'inherit',
          resize: 'vertical',
          minHeight: '100px',
          maxHeight: '200px'
        }} data-id="element-728" data-mp="u73kxg7l0w39" />
          <button type="submit" className="mp-emg-submit" style={{
          background: '#2d5a1b',
          color: 'white',
          width: '100%',
          padding: '16px',
          borderRadius: '10px',
          fontSize: '16px',
          fontWeight: 700,
          border: 'none',
          cursor: 'pointer'
        }} data-id="element-729" data-mp="2my7afdvbyh9w">
            Quiero Ayudar →
          </button>
          {formSubmitted && <p role="status" style={{
          color: '#2d5a1b',
          fontWeight: 600,
          textAlign: 'center',
          marginTop: '20px'
        }} data-id="element-730" data-mp="287q4bh7r336o">
              ¡Gracias! Te contactaremos pronto.
            </p>}
        </form>
      </section>
    </main>;
}